import { useEffect, useRef } from "react";

import { v4 as uuidV4 } from "uuid";

import { FlowVersion, Schema } from "src/api/flowTypes";
import { usePatchResourceLocks } from "src/api/flowVersionQueries";
import { notifyUI, updateSchema } from "src/api/flowVersionUpdateIndex";
import { useWorkspace } from "src/api/queries";
import { useVersionResourceLock } from "src/authoringMultiplayerLock/useVersionResourceLock";
import { ResourceType } from "src/clients/flow-api";
import { CURRENT_SESSION_ID } from "src/constants/Session";
import { useEntitySchemasJsonSchema } from "src/entities/queries";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { useFeatures } from "src/featureCatalogue/queries";
import { useCanAuthoringEditFlowVersion } from "src/hooks/useCanAuthoringEditFlowVersion";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { invalidateCompletionData } from "src/nodeEditor/api/queries";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { SchemaOptions } from "src/router/SearchParams";
import { DashboardPageParams } from "src/router/urls";
import { isCaseSchema } from "src/schema/schemaEditor/utils";
import {
  addEnrichmentConfigIfNeeded,
  buildSubSchemas,
  SchemaConverter,
} from "src/schema/schemaMappingUtils";
import { useCurrentUserId } from "src/store/AuthStore";
import { hashObject } from "src/utils/hashObject";
import { useParamsDecode } from "src/utils/useParamsDecode";

const SYNC_LOCK_DURATION_SECONDS = 30;

// $defs keys that buildSubSchemas owns. Other $defs (e.g. case_properties@*) live
// alongside but are out-of-scope for sync — never compare or rewrite them.
const isSyncedDefKey = (key: string): boolean =>
  key === "FeaturesGlobal" ||
  key.startsWith("Events@") ||
  key.startsWith("Entities@") ||
  key.startsWith("Features@");

const pickSyncedDefs = (
  defs: Record<string, unknown> | undefined,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(defs ?? {}).filter(([key]) => isSyncedDefKey(key)),
  );

export const useSyncInputSchemaDrift = (flowVersion: FlowVersion): void => {
  const canEdit = useCanAuthoringEditFlowVersion();
  const { entitiesEnabled, featuresEventsEnabled } = useWorkspaceFeatureGates();
  const { workspace } = useWorkspaceContext();
  const userId = useCurrentUserId();

  const { wsId } = useParamsDecode<DashboardPageParams>();
  const { isSuccess: workspaceGatesReady } = useWorkspace(wsId);

  const { data: entitySchemas, isSuccess: entitiesReady } =
    useEntitySchemasJsonSchema({
      baseUrl: workspace.base_url!,
      options: { enabled: canEdit && entitiesEnabled },
    });
  const { data: eventSchemas, isSuccess: eventsReady } = useEventSchemas({
    enabled: canEdit && featuresEventsEnabled,
  });
  const { data: features, isSuccess: featuresReady } = useFeatures({
    options: { enabled: canEdit && featuresEventsEnabled },
  });

  const { lockedByOtherUser } = useVersionResourceLock(
    ResourceType.SCHEMA,
    flowVersion.id,
  );
  const { mutateAsync: patchResourceLocks } = usePatchResourceLocks();

  // Track the last version id we synced so switching versions (the wrapper is
  // not remounted on version change) re-arms the sync for the new draft, while
  // the post-write setQueryData refresh still doesn't refire (same id).
  const lastSyncedVersionIdRef = useRef<string | null>(null);
  const inputSchema = flowVersion.input_schema;

  // The freshest version id + input schema seen by render. The background write
  // reads this (not the effect's snapshot) so it merges atlas $defs into edits
  // made since open, and bails if the user switched versions mid-flight.
  const latestRef = useRef<{ id: string; schema: Schema | undefined }>({
    id: flowVersion.id,
    schema: inputSchema,
  });
  latestRef.current = { id: flowVersion.id, schema: inputSchema };

  useEffect(() => {
    if (lastSyncedVersionIdRef.current === flowVersion.id) return;
    if (!canEdit) return;
    if (lockedByOtherUser) return;
    if (!inputSchema) return;
    if (isCaseSchema(inputSchema)) {
      lastSyncedVersionIdRef.current = flowVersion.id;
      return;
    }
    if (!userId) return;
    if (!workspaceGatesReady) return;

    if (entitiesEnabled && !entitiesReady) return;
    if (featuresEventsEnabled && (!eventsReady || !featuresReady)) return;

    // Pin the version this sync targets. The effect re-runs (and re-arms via the
    // ref) when flowVersion.id changes, but the async write below can outlive a
    // version switch — bind lock and write to this id, not the latest render's.
    const versionId = flowVersion.id;

    // Merge the fresh atlas $defs into the given schema, or return null when the
    // synced $defs already match (nothing to write).
    const buildDriftPatch = (schema: Schema): Schema | null => {
      const ui = SchemaConverter.beToUI(schema, SchemaOptions.Input);
      const freshSubSchemas = buildSubSchemas(
        ui,
        eventSchemas ?? [],
        entitySchemas ?? {},
        features ?? [],
      );
      const freshSyncedDefs = pickSyncedDefs(freshSubSchemas);
      const existingSyncedDefs = pickSyncedDefs(
        (schema as { $defs?: Record<string, unknown> }).$defs,
      );

      // this is needed as otherwise undefined properties are not considered equal
      // $ref was messing up the comparison
      if (hashObject(existingSyncedDefs) === hashObject(freshSyncedDefs))
        return null;

      const backendSchema = SchemaConverter.uiToBE(ui, SchemaOptions.Input);
      return addEnrichmentConfigIfNeeded(
        {
          ...backendSchema,
          $defs: { ...backendSchema.$defs, ...freshSubSchemas },
        },
        ui,
        SchemaOptions.Input,
      );
    };

    // Set the ref before the drift short-circuit so the effect can't refire
    // once the post-mutation setQueryData updates flowVersion.input_schema.
    lastSyncedVersionIdRef.current = versionId;

    // Cheap gate on the open schema: skip the lock dance entirely when undrifted.
    if (!buildDriftPatch(inputSchema)) return;

    // Acquire the schema lock, write the patch, release the lock. Sequential
    // so we don't clobber another editor or race a concurrent sync. Background
    // task — failures are swallowed; the lock release always runs.
    const lockId = uuidV4();
    void (async () => {
      try {
        await patchResourceLocks({
          flowVersionId: versionId,
          resourceLockPatch: {
            [lockId]: {
              owner: userId,
              session: CURRENT_SESSION_ID,
              resource_type: ResourceType.SCHEMA,
              resource_id: versionId,
              duration_secs: SYNC_LOCK_DURATION_SECONDS,
            },
          },
        });

        // Re-derive the patch from the freshest schema now that we hold the
        // lock, so atlas $defs merge into any edits made since open instead of
        // overwriting them with the pre-lock snapshot. Bail if the user switched
        // versions (that draft syncs via its own effect run) or drift is gone.
        const latest = latestRef.current;
        if (latest.id !== versionId || !latest.schema) return;
        const patched = buildDriftPatch(latest.schema);
        if (!patched) return;

        const updated = await updateSchema(versionId, {
          type: SchemaOptions.Input,
          schema: patched,
        });
        notifyUI(updated);
        invalidateCompletionData(versionId);
      } catch {
        // intentional — background sync, no surface to report errors to.
      } finally {
        await patchResourceLocks({
          flowVersionId: versionId,
          resourceLockPatch: { [lockId]: null },
        }).catch(() => {});
      }
    })();
  }, [
    canEdit,
    lockedByOtherUser,
    inputSchema,
    userId,
    workspaceGatesReady,
    entitiesEnabled,
    featuresEventsEnabled,
    entitiesReady,
    eventsReady,
    featuresReady,
    entitySchemas,
    eventSchemas,
    features,
    flowVersion.id,
    patchResourceLocks,
  ]);
};
